import type { RedirectToResult, Transition } from "@uirouter/react";

import { FiltersEnum } from "app/components/filters-bar/utils";
import { withAllContextHoc } from "app/components/React/HOCs/withAllContextsHOC";
import {
    SI_ROOT_ROUTE,
    WEBSITE_ANALYSIS_AUDIENCE_INTERESTS,
    WEBSITE_ANALYSIS_COMPETITORS,
    WEBSITE_ANALYSIS_DEMOGRAPHICS,
    WEBSITE_ANALYSIS_FOLDERS,
    WEBSITE_ANALYSIS_GEOGRAPHY,
    WEBSITE_ANALYSIS_MARKETING_CHANNELS,
    WEBSITE_ANALYSIS_MARKETING_CHANNELS_BETA,
    WEBSITE_ANALYSIS_PAGE_ROUTE_URL,
    WEBSITE_ANALYSIS_PAGES,
    WEBSITE_ANALYSIS_PERFORMANCE,
    WEBSITE_ANALYSIS_ROOT,
    WEBSITE_ANALYSIS_SUBDOMAINS,
    WEBSITE_ANALYSIS_TECHNOLOGIES,
    WEBSITE_ANALYSIS_TRAFFIC_ENGAGEMENT,
} from "app/pages/sales-intelligence/constants/routes";
import settingsService from "app/pages/sales-intelligence/modules/company-analysis/services/settings/settingsService";
import TechnologiesWebsiteSectionRoot from "app/pages/sales-intelligence/pages/company-analysis/containers/technologies/TechnologiesSectionRoot/TechnologiesWebsiteSectionRoot";
import { WebAnalysisGeographyRoot } from "app/pages/sales-intelligence/pages/company-analysis/containers/WebAnalysisGeographyRoot/WebAnalysisGeographyRoot";
import { WebsitePerformanceRoot } from "app/pages/sales-intelligence/pages/company-analysis/containers/WebsitePerformanceRoot/WebsitePerformanceRoot";
import { DISPLAY_GROUP_CONFIG } from "app/pages/sales-intelligence/pages/company-analysis/routing/marketing-chanels/display-group-config";
import { REFERRAL_GROUP_CONFIG } from "app/pages/sales-intelligence/pages/company-analysis/routing/marketing-chanels/referral-group-config";
import { SEARCH_GROUP_CONFIG } from "app/pages/sales-intelligence/pages/company-analysis/routing/marketing-chanels/search-group-config";
import { SOCIAL_GROUP_CONFIG } from "app/pages/sales-intelligence/pages/company-analysis/routing/marketing-chanels/social-group-config";
import CompetitiveLandscapePage from "app/pages/sales-intelligence/sub-modules/account-overview/competitive-landscape/components/CompetitiveLandscapePage/CompetitiveLandscapePage";
import AudienceInterests from "app/pages/website-analysis/audience-interests/single/AudienceInterests/AudienceInterests";
import { DemographicsUserBased } from "app/pages/website-analysis/demographicsUserBased/DemographicsUserBased";
import { Folders } from "app/pages/website-analysis/folders/Folders";
import { SalesWebsiteAnalysisRedesignModuleFrame } from "app/pages/website-analysis/frame/SalesWebsiteAnalysisRedesignModuleFrame";
import { WebAnalysisPagesContainer } from "app/pages/website-analysis/pages/WebAnalysisPagesContainer";
import WebAnalysisSubdomainsContainer from "app/pages/website-analysis/subdomains/WebAnalysisSubdomainsContainer";
import { onEnterRemoveComparedDuration } from "app/pages/website-analysis/config/onEnterRemoveComparedDuration";
import { MmxBetaContainer } from "app/pages/website-analysis/traffic-sources/mmx/mmx-beta/MmxBetaContainer";
import { MmxContainer } from "app/pages/website-analysis/traffic-sources/mmx/MmxContainer";
import { WebsiteAnalysisTrafficContainerConnected } from "app/pages/website-analysis/TrafficAndEngagement/WebsiteAnalysisTrafficContainer";
import { filterConsistencyParams } from "app/routes/handlers/filter-consistency/filterConsistencyUtils";
import {
    onRedirectWebsiteAnalysis,
    onRedirectWebsiteAnalysisWithTotalWebSource,
} from "app/routes/handlers/filter-consistency/pipes/website-analysis/onRedirectWebsiteAnalysis";
import { fallback } from "app/routes/handlers/redirectFunctions/fallback";
import { redirectPipe } from "app/routes/handlers/redirectFunctions/redirectPipe";
import { PdfExportService } from "app/services/PdfExportService";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { resolvers } from "single-spa/react-app/config/resolvers/resolvers";
import { trafficAndEngagementQueryParams } from "single-spa/react-app/config/solutions25/competitionConfig";
import {
    ENGAGEMENT_MONTH_TO_DATE_QUERY_PARAM,
    TRAFFIC_MONTH_TO_DATE_QUERY_PARAM,
} from "single-spa/react-app/config/solutions25/competitionConst";
import type { IRouterState } from "single-spa/react-app/router/IRouterState";

const getSharedWebsiteConfig = () => ({
    homeState: "salesIntelligence-home",
    trackingId: {
        section: "Account Review",
        subSection: "website",
        subSubSection: "website overview",
    },
});

export const WEBSITE_STATE_CONFIG: Record<
    string,
    IRouterState & {
        isPdfDownloadButton?: boolean;
        pdfDownloadsMethod?: () => Promise<any>;
        childStates?: any;
        promoteCompareForStateTitle?: string;
        promoteCompareForStateSubtitle?: string;
    }
> = {
    [WEBSITE_ANALYSIS_ROOT]: {
        abstract: true,
        url: WEBSITE_ANALYSIS_PAGE_ROUTE_URL,
        parent: SI_ROOT_ROUTE,
        component: withAllContextHoc(SalesWebsiteAnalysisRedesignModuleFrame),
        get configId(): string {
            return settingsService.getComponentsConfigId();
        },
        data: {
            menuId: "websites",
            filtersConfig: {
                duration: FiltersEnum.ENABLED,
                country: FiltersEnum.ENABLED,
                webSource: FiltersEnum.ENABLED,
            },
        },
    },
    [WEBSITE_ANALYSIS_PERFORMANCE]: {
        stateId: WEBSITE_ANALYSIS_PERFORMANCE,
        parent: WEBSITE_ANALYSIS_ROOT,
        url: "/overview/website-performance/:key/:isWWW/:country/:duration?webSource",
        component: WebsitePerformanceRoot,
        data: {
            gaSupport: true,
            menuId: "websites",
            filtersConfig: {
                duration: FiltersEnum.ENABLED,
                country: FiltersEnum.ENABLED,
                webSource: FiltersEnum.ENABLED,
            },
        },
        worldwideOnly: true,
        pageId: {
            section: "website",
            subSection: "worldwideOverview",
            subSubSection: undefined,
        },
        pageTitle: "analysis.overview.performance.title",
        isPdfDownloadButton: true,
        pdfDownloadsMethod: () => PdfExportService.downloadPaidPdfFedService("wwo.report.link", "wwo.report.title"),
        resolve: [resolvers.siteInfo],
        hidePageTitle: true,
        params: {
            ...filterConsistencyParams,
        },
        defaultQueryParams: {
            webSource: "Total",
            duration: "3m",
            country: 999,
            isWWW: "*",
        },
        stateNameForEductionBar: "competitiveanalysis_website_overview_websiteperformance",
        reloadOnSearch: false,
        isUSStatesSupported: true,
        redirectTo: redirectPipe(onRedirectWebsiteAnalysis()) as (transition: Transition) => RedirectToResult,
        configIdDeps: ["MobileWebWorldwide"],
        ...getSharedWebsiteConfig(),
    },
    [WEBSITE_ANALYSIS_TECHNOLOGIES]: {
        stateId: WEBSITE_ANALYSIS_TECHNOLOGIES,
        parent: WEBSITE_ANALYSIS_ROOT,
        url: "/technologies/:key",
        params: {
            comparedDuration: "",
            isWWW: "*",
        },
        component: TechnologiesWebsiteSectionRoot,
        periodOverPeriodEnabled: false,
        // configId: "WebTechnographics",
        pageId: {
            section: "website",
            subSection: "overview",
            subSubSection: "technographics",
        },
        pageTitle: "analysis.overview.technographics.title",
        pageSubtitle: "analysis.overview.technographics.subtitle",
        resolve: [resolvers.siteInfo],
        data: {
            gaSupport: false,
            trackPageViewOnSearchUpdate: false,
            filtersConfig: {
                duration: FiltersEnum.HIDDEN,
                country: FiltersEnum.HIDDEN,
                webSource: FiltersEnum.HIDDEN,
            },
        },
        hidePageTitle: true,
        reloadOnSearch: false,
        isUSStatesSupported: false,
        skipDurationCheck: true,
        skipCountryCheck: true,
        ...getSharedWebsiteConfig(),
    },
    [WEBSITE_ANALYSIS_COMPETITORS]: {
        stateId: WEBSITE_ANALYSIS_COMPETITORS,
        parent: WEBSITE_ANALYSIS_ROOT,
        url: "/overview/competitive-landscape/:key/:isWWW/:country/:duration?similarsites_filters&similarsites_orderby&selectedSite",
        component: CompetitiveLandscapePage,
        resolve: [resolvers.siteInfo],
        pageId: {
            section: "website",
            subSection: "competitors",
            subSubSection: "similarsites",
        },
        icon: "sw-icon-similarsites",
        pageTitle: "analysis.overview.competitors.similarsites.title",
        isVirtualSupported: false,
        isUSStatesSupported: true,
        // configId: "WebsiteCompetitors",
        reloadOnSearch: true,
        hideCalendar: true,
        overrideDatepickerPreset: ["3m"],
        minDurationRange: 3,
        defaultQueryParams: {
            duration: "3m",
            isWWW: "*",
            country: "999",
        },
        params: {
            ...filterConsistencyParams,
        },
        data: {
            filtersConfig: {
                duration: FiltersEnum.DISABLED,
                country: FiltersEnum.DISABLED,
            },
        },
        redirectTo: redirectPipe(onRedirectWebsiteAnalysis()) as (transition: Transition) => RedirectToResult,
        ...getSharedWebsiteConfig(),
    },
    [WEBSITE_ANALYSIS_TRAFFIC_ENGAGEMENT]: {
        stateId: WEBSITE_ANALYSIS_TRAFFIC_ENGAGEMENT,
        parent: WEBSITE_ANALYSIS_ROOT,
        url: `/traffic-engagement/:isWWW/:country/:duration?${trafficAndEngagementQueryParams}`,
        params: {
            [TRAFFIC_MONTH_TO_DATE_QUERY_PARAM]: "false",
            [ENGAGEMENT_MONTH_TO_DATE_QUERY_PARAM]: "false",
            ...filterConsistencyParams,
            comparedDuration: "",
        },
        defaultQueryParams: {
            duration: "3m",
            isWWW: "*",
            country: 999,
            comparedDuration: "",
            category: "no-category",
            webSource: devicesTypes.TOTAL,
        },
        component: WebsiteAnalysisTrafficContainerConnected,
        periodOverPeriodEnabled: true,
        redirectTo: redirectPipe(onRedirectWebsiteAnalysisWithTotalWebSource()) as (
            transition: Transition,
        ) => RedirectToResult,
        configIdDeps: ["MobileWeb", "TrafficAndEngagement"],
        pageId: {
            section: "website",
            subSection: "audience",
            subSubSection: "overview",
        },
        pageTitle: "analysis.traffic.engagement.title",
        resolve: [resolvers.siteInfo],
        data: {
            gaSupport: true,
            trackPageViewOnSearchUpdate: false,
            filtersConfig: {
                duration: FiltersEnum.ENABLED,
                country: FiltersEnum.ENABLED,
                webSource: FiltersEnum.ENABLED,
            },
        },
        childStates: {
            desktop: { configId: "AudienceOverview" },
            mobileweb: {
                configId: "AudienceOverview",
            },
            total: {
                configId: "AudienceOverview",
            },
        },
        hidePageTitle: true,
        reloadOnSearch: false,
        isUSStatesSupported: true,
        ...getSharedWebsiteConfig(),
    },
    [WEBSITE_ANALYSIS_MARKETING_CHANNELS]: {
        stateId: WEBSITE_ANALYSIS_MARKETING_CHANNELS,
        params: {
            ...filterConsistencyParams,
            comparedDuration: "",
            webSource: "Total",
        },
        parent: WEBSITE_ANALYSIS_ROOT,
        url:
            // eslint-disable-next-line max-len
            "/traffic-overview/:key/:isWWW/:country/:duration/:comparedDuration?overviewTable_filters&category&webSource?channelAnalysisMtd?channelAnalysisGranularity?channelAnalysisChannel?channelAnalysisMetric?tableCategory?sourceType?unboundedVisits",
        component: MmxContainer,
        periodOverPeriodEnabled: true,
        data: {
            filtersConfig: {
                duration: FiltersEnum.ENABLED,
                country: FiltersEnum.ENABLED,
                webSource: FiltersEnum.ENABLED,
            },
        },
        redirectTo: redirectPipe(onRedirectWebsiteAnalysis()) as (transition: Transition) => RedirectToResult,
        resolve: [resolvers.siteInfo],
        pageId: {
            section: "website",
            subSection: "traffic",
            subSubSection: "overview",
        },
        icon: "sw-icon-overview",
        pageTitle: "analysis.traffic.marketing.channels.title",
        isUSStatesSupported: true,
        hidePageTitle: true,
        reloadOnSearch: false,
        defaultQueryParams: {
            category: "no-category",
            webSource: "Desktop",
            country: 999,
            isWWW: "*",
        },
        childStates: {
            desktop: {
                configId: "MarketingMixComparePeriodOverPeriod",
            },
            mobileweb: {
                configId: "MarketingMixMobile",
            },
            total: {
                configId: "MarketingMixTotal",
            },
        },
        configIdDeps: ["MobileMarketingMix", "IndustryAnalysis"],
        ...getSharedWebsiteConfig(),
    },
    [WEBSITE_ANALYSIS_MARKETING_CHANNELS_BETA]: {
        stateId: WEBSITE_ANALYSIS_MARKETING_CHANNELS_BETA,
        params: {
            ...filterConsistencyParams,
            comparedDuration: "",
        },
        parent: WEBSITE_ANALYSIS_ROOT,
        url:
            // eslint-disable-next-line max-len
            "/traffic-overview-beta/:key/:isWWW/:country/:duration/:comparedDuration?webSource?channelAnalysisMtd?channelAnalysisGranularity?channelAnalysisChannel?sourceType?tableCategory",
        component: MmxBetaContainer,
        configId: "MarketingMixTotalBeta",
        onEnter: onEnterRemoveComparedDuration,
        periodOverPeriodEnabled: false,
        data: {
            filtersConfig: {
                duration: FiltersEnum.ENABLED,
                country: FiltersEnum.ENABLED,
                webSource: FiltersEnum.ENABLED,
            },
        },
        redirectTo: redirectPipe(
            (transition) => ({
                state: WEBSITE_ANALYSIS_MARKETING_CHANNELS,
                params: { ...transition.params() },
            }),
            onRedirectWebsiteAnalysis(),
        ) as (transition: Transition) => RedirectToResult,
        resolve: [resolvers.siteInfo],
        pageId: {
            section: "website",
            subSection: "traffic",
            subSubSection: "overview",
        },
        icon: "sw-icon-overview",
        pageTitle: "analysis.traffic.marketing.channels.title",
        isUSStatesSupported: true,
        hidePageTitle: true,
        reloadOnSearch: false,
        defaultQueryParams: {
            webSource: devicesTypes.TOTAL,
            country: 999,
            isWWW: "*",
        },
        childStates: {
            total: {
                configId: "MarketingMixTotal",
            },
        },
        configIdDeps: ["IndustryAnalysis"],
        ...getSharedWebsiteConfig(),
    },
    ...SEARCH_GROUP_CONFIG,
    ...REFERRAL_GROUP_CONFIG,
    ...DISPLAY_GROUP_CONFIG,
    ...SOCIAL_GROUP_CONFIG,
    [WEBSITE_ANALYSIS_GEOGRAPHY]: {
        stateId: WEBSITE_ANALYSIS_GEOGRAPHY,
        parent: WEBSITE_ANALYSIS_ROOT,
        url: "/audience-geography/:key/:isWWW/:country/:duration?webSource?geography_filters",
        component: WebAnalysisGeographyRoot,
        redirectTo: redirectPipe(onRedirectWebsiteAnalysis()) as (transition: Transition) => RedirectToResult,
        defaultQueryParams: {
            duration: "3m",
            isWWW: "*",
            country: "999",
            webSource: "Desktop",
        },
        params: {
            ...filterConsistencyParams,
        },
        resolve: [resolvers.siteInfo],
        reloadOnSearch: true,
        pageId: {
            section: "website",
            subSection: "audience",
            subSubSection: "geography",
        },
        icon: "sw-icon-geography",
        pageTitle: "analysis.audience.geo.title",
        isUSStatesSupported: false,
        data: {
            filtersConfig: {
                country: FiltersEnum.DISABLED,
            },
        },
        ...getSharedWebsiteConfig(),
    },
    [WEBSITE_ANALYSIS_DEMOGRAPHICS]: {
        stateId: WEBSITE_ANALYSIS_DEMOGRAPHICS,
        parent: WEBSITE_ANALYSIS_ROOT,
        url: "/audience-demographics/:key/:isWWW/:country/:duration?webSource",
        component: DemographicsUserBased,
        hidePageTitle: true,
        redirectTo: redirectPipe(onRedirectWebsiteAnalysis()),

        resolve: [resolvers.siteInfo],
        pageId: {
            section: "website",
            subSection: "audience",
            subSubSection: "demographics",
        },
        defaultQueryParams: {
            isWWW: "*",
            country: 999,
            duration: "3m",
        },
        params: {
            ...filterConsistencyParams,
            webSource: devicesTypes.TOTAL,
        },
        childStates: {
            desktop: {
                configId: "WebDemographics",
            },
            mobileweb: {
                configId: "MobileWebDemographics",
            },
            total: {
                configId: "WebDemographics",
            },
        },
        configIdDeps: ["MobileWebDemographics"],
        icon: "sw-icon-demographics",
        pageTitle: "analysis.audience.demo.title",
        isUSStatesSupported: false,
        reloadOnSearch: false,
        homeState: "salesIntelligence-home",
    },
    [WEBSITE_ANALYSIS_AUDIENCE_INTERESTS]: {
        stateId: WEBSITE_ANALYSIS_AUDIENCE_INTERESTS,
        parent: WEBSITE_ANALYSIS_ROOT,
        url: "/audience-interests/:key/:isWWW/:country/:duration?webSource?audienceInterestsTable_filters&orderBy&audienceCategory&customCategory&selectedSite",
        component: AudienceInterests,
        redirectTo: redirectPipe(onRedirectWebsiteAnalysis()),
        defaultQueryParams: {
            isWWW: "*",
            country: "999",
            duration: "3m",
            webSource: "Desktop",
        },
        params: {
            ...filterConsistencyParams,
        },
        resolve: [resolvers.siteInfo],
        pageId: {
            section: "website",
            subSection: "audience",
            subSubSection: "interests",
        },
        icon: "sw-icon-audience",
        pageTitle: "analysis.audience.interests.title",
        isVirtualSupported: false,
        isUSStatesSupported: true,
        reloadOnSearch: true,
        promoteCompareForStateTitle: "analysis.audience.interests.promotion.bubble.title",
        promoteCompareForStateSubtitle: "analysis.audience.interests.promotion.bubble.subtitle",
        homeState: "salesIntelligence-home",
        childStates: {
            desktop: {
                configId: "WebAudienceInterests",
            },
            mobileweb: {
                configId: "WebMobileAudienceInterests",
            },
            total: {
                configId: "WebMobileAudienceInterests",
            },
        },
        configIdDeps: ["WebAnalysis", "MobileWebSearch", "WebMobileAudienceInterests"],
    },
    [WEBSITE_ANALYSIS_SUBDOMAINS]: {
        stateId: WEBSITE_ANALYSIS_SUBDOMAINS,
        parent: WEBSITE_ANALYSIS_ROOT,
        url: "/content-subdomains/:key/:isWWW/:country/:duration?webSource&selectedSite",
        component: WebAnalysisSubdomainsContainer,
        resolve: [resolvers.siteInfo],
        pageId: {
            section: "website",
            subSection: "content",
            subSubSection: "subdomains",
        },
        icon: "sw-icon-subdomains",
        pageTitle: "analysis.content.subdomains.title",
        hidePageTitle: false,
        params: {
            ...filterConsistencyParams,
        },
        defaultQueryParams: {
            webSource: "Desktop",
            duration: "3m",
            country: 999,
            isWWW: "*",
        },
        configIdDeps: ["MobileWeb"],
        isVirtualSupported: false,
        isUSStatesSupported: true,
        data: {
            trackPageViewOnSearchUpdate: false,
        },
        redirectTo: redirectPipe(onRedirectWebsiteAnalysis()) as (transition: Transition) => RedirectToResult,
        viewConfig: {
            title: "si.website-analysis.tab.subdomains",
        },
        ...getSharedWebsiteConfig(),
    },
    [WEBSITE_ANALYSIS_PAGES]: {
        stateId: WEBSITE_ANALYSIS_PAGES,
        parent: WEBSITE_ANALYSIS_ROOT,
        url: "/content-popular-v2/:key/:country/:duration?webSource?selectedTab&selectedSite",
        component: WebAnalysisPagesContainer,
        redirectTo: redirectPipe(
            fallback({
                digitalsuite: "digitalsuite_website_popular_v2",
            }),
            onRedirectWebsiteAnalysis(),
        ) as (transition: Transition) => RedirectToResult,
        hidePageTitle: true,
        configId: "PopularPagesV2",
        configIdDeps: ["MobileWeb"],
        resolve: [resolvers.siteInfo],
        pageId: {
            section: "website",
            subSection: "content",
            subSubSection: "popular",
        },
        icon: "sw-icon-page-views",
        defaultQueryParams: {
            duration: "3m",
            country: 999,
        },
        params: {
            ...filterConsistencyParams,
            webSource: "Total",
            isWWW: "*",
            comparedDuration: "",
        },
        pageTitle: "analysis.content.popular.title2",
        isVirtualSupported: false,
        isUSStatesSupported: true,
        data: {
            filtersConfig: {
                webSource: FiltersEnum.ENABLED,
            },
        },
        viewConfig: {
            title: "si.website-analysis.tab.pages",
            hideBanner: true,
        },
        ...getSharedWebsiteConfig(),
    },
    [WEBSITE_ANALYSIS_FOLDERS]: {
        stateId: WEBSITE_ANALYSIS_FOLDERS,
        parent: WEBSITE_ANALYSIS_ROOT,
        url: "/content-folders/:key/:isWWW/:country/:duration?webSource?selectedSite",
        component: Folders,
        redirectTo: redirectPipe(
            fallback({
                digitalsuite: "digitalsuite_website_folders",
            }),
            onRedirectWebsiteAnalysis(),
        ) as (transition: Transition) => RedirectToResult,
        hidePageTitle: true,
        configId: "FoldersAnalysis",
        configIdDeps: ["MobileWeb"],
        resolve: [resolvers.siteInfo],
        pageId: {
            section: "website",
            subSection: "content",
            subSubSection: "folders",
        },
        params: {
            ...filterConsistencyParams,
            webSource: "Total",
        },
        defaultQueryParams: {
            webSource: "Total",
            duration: "3m",
            country: 999,
            isWWW: "*",
        },
        icon: "sw-icon-tabs-leading-folders",
        pageTitle: "analysis.content.folders.title",
        isVirtualSupported: false,
        isUSStatesSupported: true,
        data: {
            filtersConfig: {
                webSource: FiltersEnum.HIDDEN,
            },
        },
        viewConfig: {
            title: "si.website-analysis.tab.folders",
            hideBanner: true,
        },
        ...getSharedWebsiteConfig(),
    },
};
